<template>
  <div v-if="isInitialized">
    <div v-if="hasConversations && !hasStartAt" class="alert alert-info" role="alert">
      {{ estimateMessage }}
    </div>
    <div v-else-if="!hasConversations && !hasStartAt" class="alert alert-warning" role="alert">
      No messages will be sent.
    </div>
    <exception-list v-if="hasExceptions" :items="exceptions" class="alert alert-warning" role="alert"></exception-list>
    <p>
      Send List: <strong>{{ sendListName }}</strong>
    </p>
    <el-form ref="form" :model="form" v-loading="isBusy" :rules="rules">
      <el-collapse v-model="activeCollapse" @change="handleActiveCollapse">
        <el-collapse-item title="Schedule" name="schedule">
          <el-form-item label="Start At" prop="start_at">
            <el-date-picker
                v-model="form.start_at"
                type="datetime"
                placeholder="Start"
                :format="dateFormat"
                :value-format="formDateFormat"
            ></el-date-picker>
          </el-form-item>
        </el-collapse-item>
      </el-collapse>
      <el-form-item prop="body" label="Message">
        <MessageControl
          v-model="form.body"
          :approved-message-list-url="approvedMessageListUrl"
          :can-send-sms="can.sendSms"
          :can-send-approved-sms="can.sendApprovedSms"
          class="d-inline-block w-100"
        ></MessageControl>
      </el-form-item>
      <el-form-item>
        <button
          :disabled="isSendButtonDisabled"
          @click.prevent="save"
          class="btn btn-secondary px-4"
        >Send</button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import ValidatorMixin from "../../mixins/validator";
import InputMixin from '../../mixins/inputs';
import {isFilled} from "../../helpers";
import ExceptionList from './exception-list';
import MessageControl from "../shared/message-control/control.vue";

export default {
  components: {
    MessageControl,
    ExceptionList,
  },

  mixins: [
    ValidatorMixin,
    InputMixin,
  ],

  props: {
    getFormUrl: String,
    format: {
      type: String,
      default: 'MM/DD/YY HH:mm',
    },
    excludedConversationIds: Array,
    isInitialized: Boolean,
  },

  data() {
    return {
      storeUrl: String,
      requiredTime: Number,
      sendList: Object,
      conversationCount: null,
      exceptions: [],
      form: {
        body: null,
        start_at: null,
      },
      rules: {
        body: [{validator: this.fieldExternalValidator}],
      },
      errors: {},
      isBusy: false,
      offset: null,
      now: null,
      activeCollapse: null,
      can: null,
      approvedMessageListUrl: null,
    };
  },

  computed: {
    /**
     * @return Boolean
     */
    hasConversations() {
      return this.conversationCount > 0;
    },

    /**
     * @return {boolean}
     */
    hasExceptions() {
      return isFilled(this.exceptions);
    },

    /**
     * @return {string}
     */
    formattedStatAt() {
      return this.now.format(this.format);
    },

    /**
     * @return {string}
     */
    formattedFinishAt() {
      return this.now
          .clone()
          .add(this.requiredTime, 'seconds')
          .endOf('minute')
          .format(this.format);
    },

    /**
     * @return {string}
     */
    formattedDuration() {
      return moment.duration(this.requiredTime, 'seconds').humanize();
    },

    /**
     * @return String
     */
    estimateMessage() {
      return `${this.conversationCount} messages will be sent over ${this.formattedDuration} ending at ${this.formattedFinishAt}`;
    },

    /**
     * @return {String|null}
     */
    sendListName() {
      return _.get(this.sendList, 'name', null);
    },

    /**
     * @return {Array}
     */
    except() {
      return this.excludedConversationIds || [];
    },

    /**
     * @return {String}
     */
    dateFormat() {
      return config('format.datetime');
    },
    /**
     * @return {String}
     */
    formDateFormat() {
      return config('format.isoLocal');
    },

    /**
     * @return {Boolean}
     */
    hasStartAt() {
      return !_.isNull(this.form.start_at);
    },

    /**
     * @return {boolean}
     */
    isSendButtonDisabled() {
      return _.get(this.form, 'body.mode') === 'pick';
    },
  },

  methods: {
    loadForm() {
      axios.get(this.getFormUrl, {params: {except: this.except}})
          .then(({data: {data: {requiredTime, exceptions, sendList, conversationCount, storeUrl, can, approvedMessageListUrl}}}) => {
            this.requiredTime = requiredTime;
            this.exceptions = exceptions;
            this.sendList = sendList;
            this.conversationCount = conversationCount;
            this.storeUrl = storeUrl;
            this.can = can;
            this.approvedMessageListUrl = approvedMessageListUrl;
            this.$emit('formInitialized');
          })
          .catch(({response: {data: {message}}}) => {
            const payload = _.assign({}, message ? {message} : {});
            this.$emit('formInitializationError', payload);
          });
    },

    refreshNow() {
      this.now = moment().utcOffset(this.offset);
    },

    /**
     * @return {Object}
     */
    getForm() {
      const {body, ...others} = this.form;

      return {
        _method: 'POST',
        send_list_id: this.sendList.id,
        body: _.get(body, 'text', null),
        approved_message_id: _.get(body, 'id', null),
        ...others,
      };
    },

    /**
     * @return {undefined}
     */
    save() {
      this.isBusy = true;
      axios.post(this.storeUrl, this.getForm())
          .then(this.storeSuccess)
          .catch(this.handleErrorResponse)
          .finally(() => this.isBusy = false);
    },

    /**
     * @return {undefined}
     */
    storeSuccess({data: {message = 'Successfully created!'}}) {
      this.$emit('created', {message});
    },

    /**
     * @param {Object} response
     */
    handleErrorResponse({response: {data, status}}) {
      const {errors, message} = data || {};
      if (status === 422 && errors) {
        this.errors = errors;
        this.$refs.form.validate(() => null);
        this.$message.error(message);
      } else if (message && _.isString(message)) {
        this.$message.error(message);
      }
    },

    /**
     * @param val
     * @return {undefined}
     */
    handleActiveCollapse(val) {
      this.form.start_at = null;
    },
  },

  mounted() {
    this.offset = config('timezone.base.offset');
    this.loadForm();
    this.refreshNow();
    this.timerId = setInterval(this.refreshNow, 1000);
  },

  beforeDestroy() {
    clearInterval(this.timerId);
  },
}
</script>
